import { useNavigate } from 'react-router';
import { JobListingCard } from '@tempmee/react-blocks';
import type { JobListingMenuItem } from '@tempmee/core';
import { JobPostingStatus } from '~/domain/PermanentJobs';

import { STATUS_MAP } from './Constants';
import { TJobCardProps } from './Types';
import { getJobRoleBadgeCode, getStatusDateLabel } from './utils';

export const JobCard = ({
  candidateCount,
  closedAt,
  id,
  office,
  onsiteInterviewsCount,
  professionType,
  publishedAt,
  status,
  subProfession,
  title,
}: TJobCardProps) => {
  const navigate = useNavigate();

  const isClosed = status === JobPostingStatus.Closed;
  const isActive = status === JobPostingStatus.Active;

  // TODO: this literal should come from a shared path builder (e.g.
  // `permanentJobsPaths.jobOverview(id)`). Deferred out of this scope on purpose:
  // the job-overview path is hand-built across ~14 call sites in the jobs feature,
  // so centralizing it is a feature-wide refactor rather than part of these fixes.
  const jobOverviewPath = `/permanent-jobs/job-overview/${id}`;

  // Active postings can be closed. Reuse the tested Close Job flow on the Job
  // Overview page by navigating there with a flag that auto-opens it.
  const menuItems: JobListingMenuItem[] | undefined = isActive
    ? [
        {
          label: 'Close Posting',
          testID: 'active-job-card-close',
          onSelect: () => navigate(jobOverviewPath, { state: { startCloseJobFlow: true } }),
        },
      ]
    : undefined;

  return (
    <JobListingCard
      data-cy="active-job-card"
      status={STATUS_MAP[status]}
      title={title || 'UNTITLED'}
      jobRole={getJobRoleBadgeCode(professionType, subProfession)}
      office={office?.name}
      dateLabel={getStatusDateLabel(status, { publishedAt, closedAt })}
      candidates={candidateCount ?? 0}
      // The legacy card surfaced remaining onsite-interview slots as a
      // "N INTERVIEWS LEFT" warning badge (≤3) / error (0). The redesigned block
      // promotes that count to a first-class OPEN INTERVIEWS stat instead.
      openInterviews={isClosed ? '-' : onsiteInterviewsCount ?? 0}
      menuButtonTestID={isActive ? 'active-job-card-menu' : undefined}
      menuItems={menuItems}
      onCardClick={() => navigate(jobOverviewPath)}
    />
  );
};
